/**
 * Shared palette and material cache.
 *
 * Battle Chess fielded two armies with a warm/cool split: an ivory-and-gold
 * light side against a slate-and-crimson dark side. Every model pulls its
 * colours from here so the two armies stay readable from the default camera.
 */

import * as THREE from 'three';

export const PALETTE = {
  w: {
    skin: 0xe8c9a8,
    cloth: 0xf2ead6,
    clothDark: 0xd4c7a8,
    metal: 0xcfd4dc,
    accent: 0xd8a944,
    stone: 0xd9d2c0,
    // The king's doublet and sleeves. The sculpt's own cerulean for the light
    // army; the dark army takes the same hue driven to indigo, so the two
    // read as the same garment in two liveries rather than two garments.
    royal: 0x5b93c8,
    // The king's hair and beard — cut from the same surface as his face and
    // painted separately on import (see blender/import_king.py). Its own
    // key, not borrowed: every other sculpt's ad hoc hair literal is a
    // one-off match, and this one is reused by KING_SURFACE for both.
    hair: 0xe8e4d8,
    plinth: 0x9a927e
  },
  b: {
    // Kept light enough to read against the dark board and backdrop; a truly
    // black army disappears into the scene at this exposure.
    skin: 0x9c7a5e,
    cloth: 0x4d5665,
    clothDark: 0x343a47,
    metal: 0x7d8695,
    accent: 0xc23e38,
    stone: 0x5c5d68,
    royal: 0x4a3f7a,
    // Dimmed rather than matched to the white army's hue, the same
    // "kept light enough not to vanish" reasoning already used above.
    hair: 0xb8b0a0,
    // The plinth is a shade under the figure's own stone in both armies, so
    // the eye lands on the piece rather than the disc it stands on.
    plinth: 0x3f414c
  }
};

const cache = new Map();

/** Returns a shared standard material; identical params reuse one instance. */
export function mat(color, { rough = 0.55, metal = 0.1, emissive = 0x000000 } = {}) {
  const key = `${color}|${rough}|${metal}|${emissive}`;
  let m = cache.get(key);
  if (!m) {
    m = new THREE.MeshStandardMaterial({
      color,
      roughness: rough,
      metalness: metal,
      emissive
    });
    cache.set(key, m);
  }
  return m;
}

export const metalMat = (color) => mat(color, { rough: 0.3, metal: 0.85 });
export const goldMat = (color) => mat(color, { rough: 0.25, metal: 0.95 });
export const clothMat = (color) => mat(color, { rough: 0.85, metal: 0.0 });
export const stoneMat = (color) => mat(color, { rough: 0.95, metal: 0.0 });
// Aged bronze: duller and less reflective than polished steel, so the chibi
// pawn's armour reads as cast metal rather than the knight's mirror-bright
// plate — same rough/metal pair as the editor's FINISHES.bronze entry.
export const bronzeMat = (color) => mat(color, { rough: 0.55, metal: 0.75 });

export function disposeMaterials() {
  for (const m of cache.values()) m.dispose();
  cache.clear();
}
